import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { BranchGroup, BranchStats } from "@/lib/analytics/types";
import { formatMs } from "@/lib/utils/format-duration";

interface BranchBreakdownProps {
  rows: BranchStats[];
}

/** Prefers an em-dash over "0s" for empty cells in the table layout. */
function formatDurOrDash(ms: number): string {
  return ms <= 0 ? "—" : formatMs(ms);
}

const GROUP_VARIANT: Record<BranchGroup, "default" | "secondary" | "outline"> = {
  default: "default",
  pr: "outline",
  other: "secondary",
};

export function BranchBreakdown({ rows }: BranchBreakdownProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Branches</CardTitle>
        <CardDescription>Top 10 branches by number of runs in the window</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <TooltipProvider>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Branch</TableHead>
                  <TableHead>Group</TableHead>
                  <TableHead className="text-right">Runs</TableHead>
                  <TableHead className="text-right">Success %</TableHead>
                  <TableHead className="text-right">Avg duration</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground">
                      No branches found in this window.
                    </TableCell>
                  </TableRow>
                ) : null}
                {rows.map((row) => (
                  <TableRow key={row.branch}>
                    <TableCell>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-block max-w-48 truncate font-mono text-sm align-middle cursor-default">
                            {row.branch}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="top">
                          <span className="font-mono">{row.branch}</span>
                        </TooltipContent>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Badge variant={GROUP_VARIANT[row.group]}>{row.group}</Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {row.runs.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {(row.successRate * 100).toFixed(1)}%
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatDurOrDash(row.avgDurationMs)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TooltipProvider>
        </div>
      </CardContent>
    </Card>
  );
}
